import { Component, Input, OnChanges } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserService } from '../../../Dashboard/_services/users.service';
import { AlertService } from '../../../Dashboard/_services/alert.service';

// "Set a new password" for someone else - super admins only. Drop it into any
// user dialog: <app-set-password [userId]="id" [name]="'Ajith'"></app-set-password>.
// It shows nothing for anyone who isn't a super admin (the server checks too).
@Component({
    selector: 'app-set-password',
    imports: [FormsModule, MatIconModule, MatTooltipModule],
    templateUrl: './set-password.component.html'
})
export class SetPasswordComponent implements OnChanges {
  @Input() userId!: number;
  @Input() name = 'this user';

  isSuperAdmin = false;
  open = false;
  password = '';
  visible = false;
  saving = false;
  // Kept after saving so the new password can still be copied to share.
  savedPassword = '';

  constructor(
    private userService: UserService,
    private alertService: AlertService,
  ) {
    this.isSuperAdmin = this.userService.isSuperAdmin();
  }

  ngOnChanges(): void {
    this.reset();
  }

  private reset(): void {
    this.open = false;
    this.password = '';
    this.visible = false;
    this.savedPassword = '';
  }

  get tooShort(): boolean {
    return this.password.length > 0 && this.password.length < 8;
  }

  // A strong, easy-to-read password (no look-alike characters like l/1/O/0).
  generate(): void {
    const lower = 'abcdefghijkmnopqrstuvwxyz';
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const digits = '23456789';
    const symbols = '!@#$%&*?';
    const all = lower + upper + digits + symbols;
    const pick = (chars: string) => chars[this.randomInt(chars.length)];
    const chars = [pick(lower), pick(upper), pick(digits), pick(symbols)];
    while (chars.length < 14) chars.push(pick(all));
    // Shuffle so the guaranteed characters aren't always first.
    for (let i = chars.length - 1; i > 0; i--) {
      const j = this.randomInt(i + 1);
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    this.password = chars.join('');
    this.visible = true;
  }

  private randomInt(max: number): number {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return buf[0] % max;
  }

  async copy(value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      this.alertService.alert('Password copied', 'success');
    } catch {
      this.alertService.alert('Could not copy - select it and copy by hand', 'error');
    }
  }

  save(): void {
    if (this.password.length < 8) {
      this.alertService.alert('The password must be at least 8 characters', 'error');
      return;
    }
    this.saving = true;
    this.userService.adminSetPassword(this.userId, this.password).subscribe({
      next: () => {
        this.saving = false;
        this.savedPassword = this.password;
        this.password = '';
        this.alertService.alert(`Password updated for ${this.name}`, 'success');
      },
      error: (error) => {
        this.saving = false;
        this.alertService.alert(error?.error?.message || 'Could not set the password', 'error');
      },
    });
  }

  close(): void {
    this.reset();
  }
}
